/**
 * MAISON ARPENT — la galerie de la fiche produit.
 * ---------------------------------------------------------------------------
 * Ce fichier ne contient AUCUNE mécanique de carrousel ni de visionneuse : tout
 * ce qui est difficile (inertie du glisser, pincer-zoomer, piège de focus,
 * annonces ARIA) est délégué à deux bibliothèques MIT vendues dans `assets/` :
 *
 *   - **Swiper 11**      — le carrousel du cadre.
 *   - **PhotoSwipe 5**   — le plein écran.
 *
 * Il ne reste ici que le raccordement à Shopify :
 *   1. les vignettes pilotent le carrousel, et le carrousel remet la bonne
 *      vignette en surbrillance ;
 *   2. le plein écran s'ouvre sur la diapositive cliquée, et au retour la
 *      galerie se cale sur l'image qu'on regardait ;
 *   3. au changement de COLORIS, on remplace la galerie par celle du serveur —
 *      c'est le geste d'Horizon (`media-gallery.js`) ; au changement de TAILLE,
 *      les médias sont les mêmes, donc **on ne rembobine pas**.
 *
 * Balisage : `snippets/arpent-galerie.liquid` · Styles : `assets/arpent-galerie.css`
 */

/* Les deux bibliothèques sont chargées À LA DEMANDE, pas à l'import : ce fichier
   est déclaré globalement (`snippets/scripts.liquid`, comme `media-gallery.js`
   d'Horizon) pour que la galerie vive aussi dans la fenêtre d'ajout rapide — un
   `<script>` injecté par une réponse AJAX ne s'exécute jamais. Il ne doit donc
   rien peser sur une page qui n'a pas de galerie. */

/** Nom de l'événement du bus standard Shopify. */
const CHANGEMENT_DE_VARIANTE = 'shopify:product:select';

class Galerie extends HTMLElement {
  /** @type {any} */ #carrousel = null;
  /** @type {any} */ #visionneuse = null;
  /** @type {AbortController} */ #ecoutes = new AbortController();

  connectedCallback() {
    this.#brancherVignettes();
    this.#brancherVariantes();
    this.#monter();
  }

  /** Charge les bibliothèques, puis monte le carrousel et le plein écran. */
  async #monter() {
    const jeton = this.#ecoutes.signal;

    const [{ default: Swiper }, { default: PhotoSwipeLightbox }] = await Promise.all([
      import('@arpent/swiper'),
      import('@arpent/photoswipe-lightbox'),
    ]);

    /* L'élément a pu quitter le document pendant le chargement — un changement
       de coloris, une fenêtre refermée. */
    if (jeton.aborted || !this.isConnected) return;

    this.#monterCarrousel(Swiper);
    this.#monterVisionneuse(PhotoSwipeLightbox);
  }

  disconnectedCallback() {
    this.#ecoutes.abort();
    this.#visionneuse?.destroy();
    this.#visionneuse = null;
    this.#carrousel?.destroy(true, true);
    this.#carrousel = null;
  }

  get #scene() {
    return this.querySelector('[data-arpent="scene"]');
  }

  get #vignettes() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-arpent="vignette"]')]);
  }

  get nombre() {
    return Number(this.dataset.nombre || 0);
  }

  // --- 1. Le carrousel -----------------------------------------------------

  #monterCarrousel(Swiper) {
    const scene = this.#scene;
    if (!scene || this.nombre < 2) return;

    this.#carrousel = new Swiper(scene, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 380,
      watchOverflow: true,
      threshold: 4,
      /* Une galerie de 2 ou 3 images ne boucle pas : on doit sentir qu'on est
         au bout. C'est aussi ce qui garde l'ordre du DOM aligné sur les index
         de PhotoSwipe — une boucle Swiper clone les diapositives. */
      loop: false,
      navigation: {
        prevEl: scene.querySelector('[data-arpent="precedent"]'),
        nextEl: scene.querySelector('[data-arpent="suivant"]'),
      },
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: { enabled: true },
      on: {
        slideChange: () => this.#refleter(),
      },
    });

    this.#refleter();
  }

  /** Remet le compteur et la vignette courante d'accord avec le carrousel. */
  #refleter() {
    const rang = this.#carrousel?.activeIndex ?? 0;

    const compteur = this.querySelector('[data-arpent="rang"]');
    if (compteur) compteur.textContent = String(rang + 1);

    for (const [index, vignette] of this.#vignettes.entries()) {
      const courante = index === rang;
      vignette.setAttribute('aria-current', courante ? 'true' : 'false');
      if (courante) {
        vignette.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }
  }

  #brancherVignettes() {
    const { signal } = this.#ecoutes;

    for (const vignette of this.#vignettes) {
      vignette.addEventListener(
        'click',
        () => {
          const rang = Number(vignette.dataset.rang || 0);
          this.#carrousel?.slideTo(rang);
        },
        { signal }
      );
    }
  }

  // --- 2. Le plein écran ---------------------------------------------------

  /** La feuille de PhotoSwipe n'a rien à faire dans l'entête : elle ne sert
      qu'au plein écran, et elle bloquerait l'affichage de la première image. */
  #poserFeuillePleinEcran() {
    const adresse = this.dataset.feuillePleinEcran;
    if (!adresse || document.querySelector(`link[href="${adresse}"]`)) return;

    const lien = document.createElement('link');
    lien.rel = 'stylesheet';
    lien.href = adresse;
    document.head.append(lien);
  }

  #monterVisionneuse(PhotoSwipeLightbox) {
    const scene = this.#scene;
    if (!scene) return;

    this.#poserFeuillePleinEcran();

    this.#visionneuse = new PhotoSwipeLightbox({
      gallery: scene,
      children: 'a.arpent-vue__lien',
      pswpModule: () => import('@arpent/photoswipe'),
      bgOpacity: 1,
      padding: { top: 24, bottom: 24, left: 24, right: 24 },
      showHideAnimationType: 'zoom',
      wheelToZoom: true,
      /* Une image de 2 400 px sur un écran de portable : la vue d'arrivée doit
         montrer la pièce entière, pas un gros plan. */
      initialZoomLevel: 'fit',
      secondaryZoomLevel: 1.6,
      maxZoomLevel: 3,
    });

    /* Au retour du plein écran, la galerie se cale sur l'image qu'on regardait :
       sans ça, on ferme sur la 3e image et on retrouve la 1re. */
    this.#visionneuse.on('close', () => {
      const rang = this.#visionneuse?.pswp?.currIndex;
      if (typeof rang === 'number') this.#carrousel?.slideTo(rang, 0);
    });

    this.#visionneuse.init();
  }

  // --- 3. Le changement de variante ---------------------------------------

  #brancherVariantes() {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    section.addEventListener(CHANGEMENT_DE_VARIANTE, this.#surVariante, { signal: this.#ecoutes.signal });
  }

  /** @param {any} evenement */
  #surVariante = (evenement) => {
    /* Les cartes produit des recommandations émettent le même événement, et il
       remonte jusqu'ici. Il n'est pas pour nous. */
    if (!(evenement.target instanceof Element) || evenement.target.closest('product-card')) return;

    evenement.promise
      ?.then(({ detail }) => {
        const neuve = detail?.html?.querySelector('arpent-galerie');
        if (!neuve) return;

        /* Changement de TAILLE : mêmes médias, même ordre. Remplacer ferait
           rembobiner la galerie sur la première image pour rien. */
        if (neuve.dataset.signature === this.dataset.signature) return;

        this.replaceWith(neuve);
      })
      .catch((erreur) => {
        if (erreur?.name !== 'AbortError') console.warn('[arpent-galerie]', erreur);
      });
  };
}

if (!customElements.get('arpent-galerie')) {
  customElements.define('arpent-galerie', Galerie);
}
